import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import {
  EncryptionState,
  KeyGenerationResponse,
  EncryptionActionResponse,
  ChangeKeyResponse,
} from '../models/encryption-state.model';
import { ENDPOINTS_ENCRYPTION } from '../constants/api-endpoints';
import { IndexedDBStorageService } from './indexdb-storage.service';
import { RSAEncryptionUtil } from '../utils/rsa-encryption.util';
import { ApiError, ApiResponse } from '../models/api-response.model';

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  private readonly http = inject(HttpClient);
  private readonly indexedDBStorage = inject(IndexedDBStorageService);

  // Encrypted key storage (in memory for current session)
  private encryptedKeyCache: string | null = null;

  // Signal-based state management (Angular 20+)
  private readonly encryptionState = signal<EncryptionState>({
    isLocked: true, // Will be updated after checking IndexedDB
    isProcessing: false,
    currentKey: null, // Plaintext key (in memory only, not persisted)
    lastUpdated: undefined,
  });

  // Expose writable signal directly (NO asReadonly - as per project rules)
  getEncryptionState = this.encryptionState;
  private initializationStarted = false;

  // Signal to track initialization status
  readonly isReady = signal<boolean>(false);

  constructor() {
    // Trigger initialization but don't await it
    // This avoids async operation in constructor
    this.initializeFromStorage();
  }

  /**
   * Initialize encryption state from IndexedDB
   * Called automatically on construction, but doesn't block constructor
   */
  private initializeFromStorage(): void {
    if (this.initializationStarted) {
      return;
    }
    this.initializationStarted = true;

    // Execute async operation outside constructor context
    void (async () => {
      try {
        const encryptedKey = await this.indexedDBStorage.getEncryptedKey();
        if (encryptedKey) {
          this.encryptedKeyCache = encryptedKey;
          // Note: We don't have the plaintext key, but we know data is unlocked
          this.encryptionState.update(state => ({
            ...state,
            isLocked: false,
          }));
        }
      } catch {
        // Silently fail - will remain locked state
      } finally {
        // Signal that initialization is complete
        this.isReady.set(true);
      }
    })();
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Get stored encrypted key (for HTTP headers)
   */
  getStoredEncryptionKey(): string | null {
    return this.encryptedKeyCache;
  }

  /**
   * Update encryption state with common pattern
   */
  private updateState(
    isLocked: boolean,
    isProcessing: boolean,
    currentKey: string | null = null
  ): void {
    this.encryptionState.set({
      isLocked,
      isProcessing,
      currentKey,
      lastUpdated: new Date(),
    });
  }

  /**
   * Store encrypted key in IndexedDB and cache
   */
  private async storeEncryptedKey(encryptedKey: string): Promise<void> {
    await this.indexedDBStorage.setEncryptedKey(encryptedKey);
    this.encryptedKeyCache = encryptedKey;
  }

  /**
   * Remove encrypted key from storage and cache
   */
  private async removeEncryptedKey(): Promise<void> {
    await this.indexedDBStorage.removeEncryptedKey();
    this.encryptedKeyCache = null;
  }

  /**
   * Handle errors with consistent pattern
   */
  private handleError(
    defaultMessage: string
  ): ({ error }: { error: ApiError }) => Observable<never> {
    return ({ error }: { error: ApiError }) => {
      this.encryptionState.update(state => ({ ...state, isProcessing: false }));
      const message = error?.message ?? defaultMessage;
      return throwError(() => new Error(message));
    };
  }

  // ============================================
  // API METHODS
  // ============================================

  /**
   * Fetch RSA public key from server
   */
  getPublicKey(): Observable<string> {
    return this.http
      .get<ApiResponse<string>>(ENDPOINTS_ENCRYPTION.GET_PUBLIC_KEY)
      .pipe(
        map(response => {
          return response.data ?? '';
        }),
        catchError(() =>
          throwError(() => new Error('Failed to fetch public key'))
        )
      );
  }

  /**
   * Encrypt plaintext with RSA public key
   */
  private encryptWithPublicKey(
    publicKey: string,
    plaintext: string
  ): Promise<string> {
    return RSAEncryptionUtil.encrypt(publicKey, plaintext);
  }

  /**
   * Validate current key matches the backend (without unlocking)
   */
  validateCurrentKey(
    key: string
  ): Observable<{ success: boolean; message: string }> {
    return this.getPublicKey().pipe(
      // Encrypt key with RSA public key
      switchMap(publicKey => from(this.encryptWithPublicKey(publicKey, key))),
      // Send encrypted key to server for validation
      switchMap(encryptedKey =>
        this.http
          .post<{ message: string }>(ENDPOINTS_ENCRYPTION.POST_CHECK_KEY, {
            key: encryptedKey,
          })
          .pipe(
            map(response => {
              return { success: true, message: response.message };
            })
          )
      ),
      catchError(this.handleError('Invalid encryption key'))
    );
  }

  /**
   * Unlock financial data with encryption key
   */
  unlockData(key: string): Observable<EncryptionActionResponse> {
    this.encryptionState.update(state => ({ ...state, isProcessing: true }));

    return this.getPublicKey().pipe(
      // Encrypt key with RSA public key
      switchMap(publicKey => from(this.encryptWithPublicKey(publicKey, key))),
      // Send encrypted key to server for validation
      switchMap(encryptedKey =>
        this.http
          .post<{ message: string }>(ENDPOINTS_ENCRYPTION.POST_CHECK_KEY, {
            key: encryptedKey,
          })
          .pipe(
            map(response => {
              return { encryptedKey, message: response.message };
            })
          )
      ),
      // Store encrypted key and update state
      switchMap(async ({ encryptedKey, message }) => {
        await this.storeEncryptedKey(encryptedKey);
        this.updateState(false, false, key);
        return { success: true, message };
      }),
      catchError(this.handleError('Invalid encryption key'))
    );
  }

  /**
   * Lock financial data
   */
  lockData(): Observable<EncryptionActionResponse> {
    this.encryptionState.update(state => ({ ...state, isProcessing: true }));

    return from(
      (async () => {
        await this.removeEncryptedKey();
        this.updateState(true, false);
        return {
          success: true,
          message: 'Financial data locked successfully',
        };
      })()
    );
  }

  /**
   * Generate new encryption key
   */
  generateNewKey(): Observable<KeyGenerationResponse> {
    this.encryptionState.update(state => ({ ...state, isProcessing: true }));

    return new Observable<KeyGenerationResponse>(observer => {
      try {
        // Generate 16 bytes of random data
        const randomBytes = crypto.getRandomValues(new Uint8Array(16));

        // Convert to lowercase hexadecimal string (32 characters)
        // Matches regex: /^[a-z0-9]{32}$/
        const newKey = Array.from(randomBytes)
          .map(byte => byte.toString(16).padStart(2, '0'))
          .join('');

        this.encryptionState.update(state => ({
          ...state,
          isProcessing: false,
        }));
        observer.next({ key: newKey, timestamp: new Date() });
        observer.complete();
      } catch {
        this.encryptionState.update(state => ({
          ...state,
          isProcessing: false,
        }));
        observer.error(new Error('Failed to generate encryption key'));
      }
    });
  }

  /**
   * Apply new encryption key
   */
  applyNewKey(
    currentKey: string,
    newKey: string
  ): Observable<EncryptionActionResponse> {
    this.encryptionState.update(state => ({ ...state, isProcessing: true }));

    return this.getPublicKey().pipe(
      // Encrypt both keys with RSA public key
      switchMap(async publicKey => ({
        encryptedCurrentKey: await this.encryptWithPublicKey(
          publicKey,
          currentKey
        ),
        encryptedNewKey: await this.encryptWithPublicKey(publicKey, newKey),
      })),
      // Send both encrypted keys to server
      switchMap(({ encryptedCurrentKey, encryptedNewKey }) =>
        this.http
          .post<ChangeKeyResponse>(ENDPOINTS_ENCRYPTION.POST_CHANGE_KEY, {
            current: encryptedCurrentKey,
            new: encryptedNewKey,
          })
          .pipe(
            map(response => ({ encryptedNewKey, message: response.message }))
          )
      ),
      // Store new encrypted key and update state
      switchMap(async ({ encryptedNewKey, message }) => {
        await this.storeEncryptedKey(encryptedNewKey);
        this.updateState(false, false, newKey);
        return { success: true, message };
      }),
      catchError(this.handleError('Failed to change encryption key'))
    );
  }

  /**
   * Download encryption key as text file
   */
  downloadKey(key: string): void {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const blob = new Blob([key], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `encryption-key-${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Incremented whenever the unlock form should be opened (e.g. stale key).
   */
  readonly unlockPromptNonce = signal(0);

  /**
   * Clear a deprecated/invalid stored encryption key and prompt unlock UI.
   * Used when the backend rejects the current `x-encrypted-key`.
   */
  async clearInvalidKey(): Promise<void> {
    await this.removeEncryptedKey();
    this.updateState(true, false, null);
    this.unlockPromptNonce.update(nonce => nonce + 1);
  }

  /**
   * Reset encryption state (for testing)
   */
  async resetState(): Promise<void> {
    await this.removeEncryptedKey();
    this.encryptionState.set({
      isLocked: true,
      isProcessing: false,
      currentKey: null,
      lastUpdated: undefined,
    });
  }
}
